import React from 'react';

interface ClarificationStatusMessageProps {
  canAddClarifications: boolean;
  canReplyOnly: boolean;
  isClarificationReceived: boolean;
  hasClarifications: boolean;
}

/**
 * Renders the appropriate informational message based on the user's
 * permission state and the current clarification workflow status.
 *
 * Only one message is shown at a time, with the following priority:
 *   1. Reply-only mode (customer awaiting clarification)
 *   2. Read-only — response submitted, awaiting admin review
 *   3. No permission — non-admin user outside clarification workflow
 */
const ClarificationStatusMessage: React.FC<ClarificationStatusMessageProps> = ({
  canAddClarifications,
  canReplyOnly,
  isClarificationReceived,
  hasClarifications,
}) => {
  if (canReplyOnly) {
    return (
      <div className="text-sm text-gray-500 bg-gray-50 p-3 rounded border border-gray-200">
        <p>
          {hasClarifications
            ? 'Please reply to the items above using the Reply button.'
            : 'No items need attention at this time.'}
        </p>
      </div>
    );
  }

  if (!canAddClarifications && isClarificationReceived) {
    return (
      <div className="text-sm text-gray-500 bg-gray-50 p-3 rounded border border-gray-200">
        <p>
          Your responses have been submitted and are awaiting review. No changes
          can be made at this time.
        </p>
      </div>
    );
  }

  if (!canAddClarifications) {
    return (
      <div className="text-sm text-gray-500 bg-gray-50 p-3 rounded border border-gray-200">
        <p>No items need attention at this time.</p>
      </div>
    );
  }

  return null;
};

export default ClarificationStatusMessage;
